import React, { useState, useEffect } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { Button } from '../components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { TrendingUp, Plus, Eye, FileText, Loader2, LogOut } from 'lucide-react';
import { toast } from 'sonner';
import axios from 'axios';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

const CreditBoostDashboard = () => {
  const { user, logout } = useAuth();
  const navigate = useNavigate();
  const [plans, setPlans] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const fetchPlans = async () => {
      try {
        const token = localStorage.getItem('careerboost_token');
        const response = await axios.get(
          `${BACKEND_URL}/api/creditboost/plans`,
          { headers: { Authorization: `Bearer ${token}` } }
        );
        setPlans(response.data.plans);
      } catch (error) {
        console.debug('Error fetching plans:', error);
      } finally {
        setLoading(false);
      }
    };
    fetchPlans();
  }, []);

  const handleLogout = () => {
    logout();
    navigate('/creditboost');
  };

  const formatDate = (dateString) => {
    return new Date(dateString).toLocaleDateString('en-US', {
      month: 'short',
      day: 'numeric',
      year: 'numeric'
    });
  };

  return (
    <div className="min-h-screen bg-slate-50">
      {/* Header */}
      <header className="bg-white border-b border-slate-200">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex items-center justify-between h-16">
            <Link to="/creditboost" className="flex items-center gap-2">
              <div className="w-8 h-8 bg-blue-600 rounded-lg flex items-center justify-center">
                <TrendingUp className="w-5 h-5 text-white" />
              </div>
              <span className="font-bold text-xl text-slate-900 hidden sm:inline" style={{ fontFamily: 'Outfit, sans-serif' }}>
                CreditBoost
              </span>
            </Link>
            <div className="flex items-center gap-2 sm:gap-4">
              <Link to="/creditboost/dispute" className="hidden sm:block">
                <Button variant="outline" size="sm">
                  <FileText className="w-4 h-4 mr-2" />
                  Dispute Letters
                </Button>
              </Link>
              <span className="text-sm text-slate-600 hidden sm:inline">Hi, {user?.name?.split(' ')[0]}</span>
              <Button variant="ghost" size="sm" onClick={handleLogout} data-testid="logout-btn">
                <LogOut className="w-4 h-4 sm:mr-2" />
                <span className="hidden sm:inline">Logout</span>
              </Button>
            </div>
          </div>
        </div>
      </header>

      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        {/* Header */}
        <div className="flex items-center justify-between mb-8">
          <div>
            <h1 className="text-3xl font-bold text-slate-900" style={{ fontFamily: 'Outfit, sans-serif' }}>
              My Credit Plans
            </h1>
            <p className="text-slate-600 mt-1">
              Track your credit improvement journey
            </p>
          </div>
          <Link to="/creditboost/generate">
            <Button className="bg-blue-600 hover:bg-blue-700" data-testid="new-plan-btn">
              <Plus className="w-4 h-4 mr-2" />
              New Action Plan
            </Button>
          </Link>
        </div>

        {/* Quick Actions */}
        <div className="grid md:grid-cols-3 gap-4 mb-8">
          <Link to="/creditboost/generate">
            <Card className="hover:shadow-lg transition-shadow cursor-pointer bg-gradient-to-br from-blue-500 to-blue-700 text-white">
              <CardContent className="p-6">
                <TrendingUp className="w-8 h-8 mb-3" />
                <h3 className="font-semibold text-lg">Get Action Plan</h3>
                <p className="text-blue-100 text-sm">Personalized credit improvement roadmap</p>
              </CardContent>
            </Card>
          </Link>
          <Link to="/creditboost/dispute">
            <Card className="hover:shadow-lg transition-shadow cursor-pointer">
              <CardContent className="p-6">
                <FileText className="w-8 h-8 mb-3 text-blue-600" />
                <h3 className="font-semibold text-lg text-slate-900">Dispute Letters</h3>
                <p className="text-slate-500 text-sm">Remove negative items from your report</p>
              </CardContent>
            </Card>
          </Link>
          <Link to="/creditboost/generate">
            <Card className="hover:shadow-lg transition-shadow cursor-pointer">
              <CardContent className="p-6">
                <TrendingUp className="w-8 h-8 mb-3 text-emerald-600" />
                <h3 className="font-semibold text-lg text-slate-900">Score Simulator</h3>
                <p className="text-slate-500 text-sm">See how actions affect your score</p>
              </CardContent>
            </Card>
          </Link>
        </div>

        {/* Plans */}
        <h2 className="text-xl font-semibold text-slate-900 mb-4">Your Action Plans</h2>
        
        {loading ? (
          <div className="flex items-center justify-center py-20">
            <Loader2 className="w-8 h-8 animate-spin text-blue-600" />
          </div>
        ) : plans.length > 0 ? (
          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
            {plans.map((plan) => (
              <Card key={plan.id} className="hover:shadow-lg transition-shadow" data-testid={`plan-card-${plan.id}`}>
                <CardHeader className="pb-3">
                  <div className="flex items-start justify-between">
                    <div className="w-10 h-10 bg-blue-100 rounded-lg flex items-center justify-center">
                      <TrendingUp className="w-5 h-5 text-blue-600" />
                    </div>
                    <span className={`text-xs px-2 py-1 rounded-full ${
                      plan.plan_type === 'complete' 
                        ? 'bg-purple-100 text-purple-700' 
                        : 'bg-blue-100 text-blue-700'
                    }`}>
                      {plan.plan_type === 'complete' ? '90-Day' : '30-Day'}
                    </span>
                  </div>
                  <CardTitle className="text-lg mt-3">
                    {plan.plan_type === 'complete' ? 'Complete Transformation' : 'Quick Fix Plan'}
                  </CardTitle>
                  <p className="text-sm text-slate-500">
                    Score: {plan.profile?.score_range || 'N/A'}
                  </p>
                </CardHeader>
                <CardContent>
                  <p className="text-xs text-slate-400 mb-4">
                    Created {formatDate(plan.created_at)}
                  </p>
                  <Button
                    variant="outline"
                    className="w-full"
                    onClick={() => navigate(`/creditboost/plans/${plan.id}`)}
                    data-testid={`view-plan-${plan.id}`}
                  >
                    <Eye className="w-4 h-4 mr-2" />
                    View Plan
                  </Button>
                </CardContent>
              </Card>
            ))}
          </div>
        ) : (
          <div className="text-center py-20">
            <div className="w-20 h-20 bg-slate-100 rounded-full flex items-center justify-center mx-auto mb-6">
              <TrendingUp className="w-10 h-10 text-slate-400" />
            </div>
            <h2 className="text-xl font-semibold text-slate-900 mb-2">No plans yet</h2>
            <p className="text-slate-600 mb-6">Create your first credit improvement action plan</p>
            <Link to="/creditboost/generate">
              <Button className="bg-blue-600 hover:bg-blue-700" data-testid="create-first-plan-btn">
                <Plus className="w-4 h-4 mr-2" />
                Get Your Action Plan
              </Button>
            </Link>
          </div>
        )}
      </div>
    </div>
  );
};

export default CreditBoostDashboard;
